/**
 * Timezone constants and configuration
 * Single source of truth for timezone mappings used across frontend and backend
 */
/**
 * Mapping from enum-style timezone names to IANA timezone identifiers
 * Used to convert database enum values (UPPERCASE_UNDERSCORE) to valid IANA timezones
 */
export const TIMEZONE_MAPPING = {
    // US Priority
    AMERICA_NEW_YORK: 'America/New_York',
    AMERICA_CHICAGO: 'America/Chicago',
    AMERICA_DENVER: 'America/Denver',
    AMERICA_LOS_ANGELES: 'America/Los_Angeles',
    // US Additional
    AMERICA_PHOENIX: 'America/Phoenix',
    AMERICA_ANCHORAGE: 'America/Anchorage',
    PACIFIC_HONOLULU: 'Pacific/Honolulu',
    // Canada
    AMERICA_TORONTO: 'America/Toronto',
    AMERICA_VANCOUVER: 'America/Vancouver',
    AMERICA_MONTREAL: 'America/Montreal',
    AMERICA_REGINA: 'America/Regina',
    // Europe
    EUROPE_LONDON: 'Europe/London',
    EUROPE_PARIS: 'Europe/Paris',
    EUROPE_BERLIN: 'Europe/Berlin',
    EUROPE_ROME: 'Europe/Rome',
    EUROPE_MADRID: 'Europe/Madrid',
    EUROPE_AMSTERDAM: 'Europe/Amsterdam',
    EUROPE_STOCKHOLM: 'Europe/Stockholm',
    EUROPE_ZURICH: 'Europe/Zurich',
    // Asia Pacific
    ASIA_TOKYO: 'Asia/Tokyo',
    ASIA_SHANGHAI: 'Asia/Shanghai',
    ASIA_HONG_KONG: 'Asia/Hong_Kong',
    ASIA_SINGAPORE: 'Asia/Singapore',
    ASIA_DUBAI: 'Asia/Dubai',
    ASIA_KOLKATA: 'Asia/Kolkata',
    // Australia/Oceania
    AUSTRALIA_SYDNEY: 'Australia/Sydney',
    AUSTRALIA_MELBOURNE: 'Australia/Melbourne',
    PACIFIC_AUCKLAND: 'Pacific/Auckland',
    // South America
    AMERICA_SAO_PAULO: 'America/Sao_Paulo',
    AMERICA_BUENOS_AIRES: 'America/Buenos_Aires',
    // Africa
    AFRICA_JOHANNESBURG: 'Africa/Johannesburg',
    AFRICA_CAIRO: 'Africa/Cairo',
};
/**
 * Comprehensive timezone aliases and search terms for UI autocomplete
 */
export const TIMEZONE_ALIASES = {
    // US Priority Timezones
    AMERICA_NEW_YORK: [
        'eastern',
        'est',
        'edt',
        'eastern standard time',
        'eastern daylight time',
        'new york',
    ],
    AMERICA_CHICAGO: [
        'central',
        'cst',
        'cdt',
        'central standard time',
        'central daylight time',
        'chicago',
    ],
    AMERICA_DENVER: [
        'mountain',
        'mst',
        'mdt',
        'mountain standard time',
        'mountain daylight time',
        'denver',
    ],
    AMERICA_LOS_ANGELES: [
        'pacific',
        'pst',
        'pdt',
        'pacific standard time',
        'pacific daylight time',
        'los angeles',
        'california',
    ],
    // US Additional
    AMERICA_PHOENIX: ['arizona', 'phoenix', 'mst', 'mountain standard time'],
    AMERICA_ANCHORAGE: [
        'alaska',
        'akst',
        'akdt',
        'alaska standard time',
        'alaska daylight time',
        'anchorage',
    ],
    PACIFIC_HONOLULU: ['hawaii', 'hst', 'hawaii standard time', 'honolulu'],
    // Canada
    AMERICA_TORONTO: ['toronto', 'eastern', 'est', 'edt', 'ontario'],
    AMERICA_VANCOUVER: ['vancouver', 'pacific', 'pst', 'pdt', 'british columbia'],
    AMERICA_MONTREAL: ['montreal', 'eastern', 'est', 'edt', 'quebec'],
    AMERICA_REGINA: [
        'saskatchewan',
        'regina',
        'saskatoon',
        'central',
        'cst',
        'central standard time',
    ],
    // Europe
    EUROPE_LONDON: [
        'london',
        'gmt',
        'bst',
        'greenwich mean time',
        'british summer time',
        'uk',
        'britain',
    ],
    EUROPE_PARIS: [
        'paris',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'france',
    ],
    EUROPE_BERLIN: [
        'berlin',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'germany',
    ],
    EUROPE_ROME: [
        'rome',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'italy',
    ],
    EUROPE_MADRID: [
        'madrid',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'spain',
    ],
    EUROPE_AMSTERDAM: [
        'amsterdam',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'netherlands',
    ],
    EUROPE_STOCKHOLM: [
        'stockholm',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'sweden',
    ],
    EUROPE_ZURICH: [
        'zurich',
        'cet',
        'cest',
        'central european time',
        'central european summer time',
        'switzerland',
    ],
    // Asia Pacific
    ASIA_TOKYO: ['tokyo', 'jst', 'japan standard time', 'japan'],
    ASIA_SHANGHAI: ['shanghai', 'cst', 'china standard time', 'beijing', 'china'],
    ASIA_HONG_KONG: ['hong kong', 'hkt', 'hong kong time'],
    ASIA_SINGAPORE: ['singapore', 'sgt', 'singapore standard time'],
    ASIA_DUBAI: ['dubai', 'gst', 'gulf standard time', 'uae'],
    ASIA_KOLKATA: ['kolkata', 'mumbai', 'ist', 'india standard time', 'india'],
    // Australia/Oceania
    AUSTRALIA_SYDNEY: [
        'sydney',
        'aest',
        'aedt',
        'australian eastern standard time',
        'australian eastern daylight time',
        'australia',
    ],
    AUSTRALIA_MELBOURNE: [
        'melbourne',
        'aest',
        'aedt',
        'australian eastern standard time',
        'australian eastern daylight time',
        'australia',
    ],
    PACIFIC_AUCKLAND: [
        'auckland',
        'nzst',
        'nzdt',
        'new zealand standard time',
        'new zealand daylight time',
        'new zealand',
    ],
    // South America
    AMERICA_SAO_PAULO: [
        'sao paulo',
        'são paulo',
        'brt',
        'brst',
        'brazil time',
        'brasilia time',
        'brazil',
    ],
    AMERICA_BUENOS_AIRES: ['buenos aires', 'art', 'argentina time', 'argentina'],
    // Africa
    AFRICA_JOHANNESBURG: [
        'johannesburg',
        'sast',
        'south africa standard time',
        'south africa',
    ],
    AFRICA_CAIRO: [
        'cairo',
        'eet',
        'eest',
        'eastern european time',
        'eastern european summer time',
        'egypt',
    ],
};
/**
 * Top US timezones for priority display in UI
 */
export const US_PRIORITY_TIMEZONES = [
    'AMERICA_NEW_YORK',
    'AMERICA_CHICAGO',
    'AMERICA_DENVER',
    'AMERICA_LOS_ANGELES',
];
/**
 * Additional common timezones for secondary display in UI
 */
export const ADDITIONAL_TIMEZONES = [
    'AMERICA_PHOENIX',
    'AMERICA_ANCHORAGE',
    'PACIFIC_HONOLULU',
    'AMERICA_TORONTO',
    'AMERICA_VANCOUVER',
    'AMERICA_MONTREAL',
    'AMERICA_REGINA',
    'EUROPE_LONDON',
    'EUROPE_PARIS',
    'EUROPE_BERLIN',
    'EUROPE_ROME',
    'EUROPE_MADRID',
    'EUROPE_AMSTERDAM',
    'EUROPE_STOCKHOLM',
    'EUROPE_ZURICH',
    'ASIA_TOKYO',
    'ASIA_SHANGHAI',
    'ASIA_HONG_KONG',
    'ASIA_SINGAPORE',
    'ASIA_DUBAI',
    'ASIA_KOLKATA',
    'AUSTRALIA_SYDNEY',
    'AUSTRALIA_MELBOURNE',
    'PACIFIC_AUCKLAND',
    'AMERICA_SAO_PAULO',
    'AMERICA_BUENOS_AIRES',
    'AFRICA_JOHANNESBURG',
    'AFRICA_CAIRO',
];
//# sourceMappingURL=timezone-constants.js.map